import { Component, inject, signal, computed, OnInit } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { ResetDemoAccountComponent } from '@shared/components';
import { Organization } from '@shared/models';
import { CustomerOverviewDialogService, UserPermissionsService } from '@shared/services';
import { AppEnvironmentState, ButtonV3Component, Environment, IconV3Component, NavigationItem } from '@gea/digital-ui-lib';
import { OrganizationsStore } from '@shared/state';
import { BRCBaseComponent } from '@brc/tab-container';
import { extractRouteFromURL } from '@brc/shared/helpers';

import { ChangeDetectionStrategy } from '@angular/core';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'gea-hrt-brc-header',
  standalone: true,
  imports: [IconV3Component, ButtonV3Component, ResetDemoAccountComponent, TranslatePipe],
  template: `
    <div class="brc-header">
      <div class="tab-name flex">
        @let tab = activeTab();

        @if (tab) {
          <h3>{{ tab.label | translate }}</h3>
        }

        @if (shouldRenderResetDemoAccountButton()) {
          <gea-hrt-reset-demo-account />
        }
      </div>

      <div class="flex">
        <a [href]="contactEmailLink()" class="contact-link" aria-label="Contact Support via Email" title="Contact Support">
          <geaui-icon-v3 file="letter" [fileSize]="16" class="icon-mail" />
          <span class="contact-text">{{ 'CONTACT_TEXT' | translate }}</span>
        </a>

        @let org = organization();
        @if (org) {
          <div class="selected-customer">
            <h3 [title]="org.name">{{ org.name }}</h3>
          </div>
        }

        @if (shouldRenderCustomerOverviewButton()) {
          <geaui-button-v3 (click)="customerOverviewDialogService.openDialog()">
            {{ 'BUTTON_OPEN_CUSTOMER_OVERVIEW' | translate }}
          </geaui-button-v3>
        }
      </div>
    </div>
  `,
  styleUrl: './header.component.scss',
})
export class BRCHeaderComponent extends BRCBaseComponent implements OnInit {
  protected readonly customerOverviewDialogService = inject(CustomerOverviewDialogService);

  private readonly userPermissions = inject(UserPermissionsService);
  private readonly DEMO_ORG_ID = 100258802;

  private readonly environmentSignal = signal<Environment | null>(null);
  private readonly selectedOrgSignal = signal<Organization | undefined>(undefined);
  private readonly orgsSignal = signal<Organization[]>([]);

  private readonly environment$ = this.store.select(AppEnvironmentState.environment);
  private readonly organizationsStore = inject(OrganizationsStore);
  private readonly selectedOrg$ = toObservable(this.organizationsStore.selected);
  private readonly orgs$ = toObservable(this.organizationsStore.organizations);

  readonly organization = computed(() => this.selectedOrgSignal());
  readonly shouldRenderResetDemoAccountButton = computed(
    () =>
      this.routeSignal().includes('fault-analysis') &&
      this.selectedOrgSignal()?.id === this.DEMO_ORG_ID &&
      this.environmentSignal() === Environment.TEST
  );

  readonly shouldRenderCustomerOverviewButton = computed(() => this.orgsSignal().length > 1);
  readonly activeTab = computed<NavigationItem | undefined>(() => this.userPermissions.getActiveTab(this.routeSignal()));

  private readonly routeSignal = signal('');

  readonly contactEmailLink = computed(() => 'mailto:Blu-Red.DOC.Support@gea.com?subject=Regarding BLU-RED-Care:');

  ngOnInit(): void {
    this.listenToRouterEvents();
    this.listenToStoreChanges();

    try {
      this.routeSignal.set(extractRouteFromURL(this.router.url) ?? '');
    } catch {
      this.routeSignal.set('');
    }
  }

  private listenToStoreChanges(): void {
    this.environment$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((env) => this.environmentSignal.set(env));
    this.selectedOrg$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((org) => this.selectedOrgSignal.set(org?.organization));
    this.orgs$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((orgs) => this.orgsSignal.set(orgs ?? []));
  }

  private listenToRouterEvents(): void {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((nav) => {
        this.routeSignal.set(extractRouteFromURL(nav.url) ?? '');
      });
  }
}
